import { useMemo, useState } from "react";
import { CartesianGrid, ComposedChart, Line, ReferenceLine, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { DASH, formatClock, formatCompact } from "../../format";

export interface LiveSeries {
  key: string;
  label: string;
  color: string;
  dashed?: boolean;
  step?: boolean;
  hidden?: boolean;
  /** only a dot at each non-null value (single events such as a failed request), no line */
  dots?: boolean;
}

export type LiveRow = { ts: number } & Record<string, number | null>;

interface LiveChartProps {
  rows: LiveRow[];
  series: LiveSeries[];
  height?: number;
  format?: (value: number) => string;
  tickFormat?: (value: number) => string;
  integer?: boolean;
  /**
   * fixed length of the x axis (ms), ending at the newest sample: the axis stays on the server's clock,
   * so a skewed browser clock cannot cut samples off; without it the axis spans the data
   */
  spanMs?: number;
  referenceLines?: { y: number; label?: string; color?: string }[];
  emptyText?: string;
  className?: string;
}

/**
 * Line chart of raw samples on a real time axis (irregular timestamps: process samples, the event loop
 * probe, single pod requests). The tooltip names the sample's exact time to the second; the legend
 * (two or more series) toggles series like the board's bucket charts.
 */
export function LiveChart({
  rows,
  series,
  height = 160,
  format = (v) => formatCompact(v),
  tickFormat = (v) => formatCompact(v),
  integer = false,
  spanMs,
  referenceLines,
  emptyText,
  className,
}: LiveChartProps) {
  const { t } = useLocalization();
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(series.filter((s) => s.hidden).map((s) => s.key)));

  const config = useMemo<ChartConfig>(
    () => Object.fromEntries(series.map((s) => [s.key, { label: s.label, color: s.color }])),
    [series],
  );

  const visible = series.filter((s) => !hidden.has(s.key));
  const hasData = rows.some((row) => visible.some((s) => typeof row[s.key] === "number"));
  const sparse = rows.length < 40;
  const newest = rows.length ? rows[rows.length - 1].ts : null;
  const domain: [number, number] | null = spanMs && newest !== null ? [newest - spanMs, newest] : null;

  const toggle = (key: string) =>
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)}>
      {series.length > 1 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1" role="group" aria-label={t("monitoring.chart.legend")}>
          {series.map((s) => {
            const off = hidden.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggle(s.key)}
                aria-pressed={!off}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-[11px] text-muted-foreground transition-opacity hover:text-foreground",
                  off && "opacity-40",
                )}
              >
                <LiveSwatch series={s} />
                {s.label}
              </button>
            );
          })}
        </div>
      )}
      <div className="relative">
        <ChartContainer config={config} className="aspect-auto w-full" style={{ height }}>
          <ComposedChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/60" />
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={domain ?? ["dataMin", "dataMax"]}
              allowDataOverflow={!!domain}
              tickLine={false}
              axisLine={false}
              tickMargin={6}
              minTickGap={36}
              tickFormatter={(value: number) => formatClock(value, false)}
              className="text-[10px]"
            />
            <YAxis
              width={46}
              tickLine={false}
              axisLine={false}
              tickMargin={4}
              allowDecimals={!integer}
              domain={[0, "auto"]}
              tickFormatter={tickFormat}
              className="text-[10px]"
            />
            <ChartTooltip
              cursor={{ stroke: "hsl(var(--border))" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0]?.payload as LiveRow | undefined;
                if (!row) return null;
                const shown = visible.filter((s) => typeof row[s.key] === "number");
                return (
                  <div className="min-w-[9rem] rounded-md border bg-popover px-2.5 py-2 text-xs shadow-md">
                    <div className="mb-1.5 font-medium tabular-nums text-foreground">{formatClock(row.ts)}</div>
                    <div className="flex flex-col gap-1">
                      {shown.length === 0 && <span className="text-muted-foreground">{DASH}</span>}
                      {shown.map((s) => (
                        <div key={s.key} className="flex items-center gap-2">
                          <LiveSwatch series={s} />
                          <span className="flex-1 text-muted-foreground">{s.label}</span>
                          <span className="font-mono tabular-nums text-foreground">{format(row[s.key] as number)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }}
            />
            {referenceLines?.map((line) => (
              <ReferenceLine
                key={`${line.y}-${line.label}`}
                y={line.y}
                stroke={line.color ?? "hsl(var(--muted-foreground))"}
                strokeDasharray="4 4"
                strokeOpacity={0.7}
                label={
                  line.label
                    ? { value: line.label, position: "insideTopRight", fontSize: 10, fill: "hsl(var(--muted-foreground))" }
                    : undefined
                }
              />
            ))}
            {visible.map((s) =>
              s.dots ? (
                <Line
                  key={s.key}
                  dataKey={s.key}
                  stroke="none"
                  connectNulls={false}
                  dot={{ r: 3, fill: s.color, stroke: "hsl(var(--card))", strokeWidth: 1 }}
                  activeDot={{ r: 4, fill: s.color, stroke: "hsl(var(--card))", strokeWidth: 1 }}
                  isAnimationActive={false}
                />
              ) : (
                <Line
                  key={s.key}
                  dataKey={s.key}
                  type={s.step ? "stepAfter" : "linear"}
                  stroke={s.color}
                  strokeWidth={s.dashed ? 1.5 : 2}
                  strokeDasharray={s.dashed ? "4 3" : undefined}
                  dot={sparse ? { r: 2, fill: s.color, strokeWidth: 0 } : false}
                  activeDot={{ r: 3.5, strokeWidth: 2, stroke: "hsl(var(--card))" }}
                  connectNulls
                  isAnimationActive={false}
                />
              ),
            )}
          </ComposedChart>
        </ChartContainer>
        {!hasData && emptyText && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center pb-6 text-center text-xs text-muted-foreground">
            {emptyText}
          </div>
        )}
      </div>
    </div>
  );
}

function LiveSwatch({ series }: { series: LiveSeries }) {
  if (series.dots) {
    return <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: series.color }} />;
  }
  return (
    <span
      className="inline-block w-3.5 shrink-0"
      style={{ borderTop: `2px ${series.dashed ? "dashed" : "solid"} ${series.color}` }}
    />
  );
}
